import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent, ResponsiveFooter, ResponsiveHeader } from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { CreditCardIcon, LightBulbIcon, ShieldIcon, SubscriptionIcon, CarIcon, MedicalIcon, BankIcon, DocumentsIcon } from '../ui/Icons';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface ExpenseCategory {
  key: 'credit_card' | 'utilities' | 'insurance' | 'subscriptions' | 'transportation' | 'healthcare' | 'other';
  label: string;
  Icon: React.FC<{ className?: string }>;
  description: string;
}

interface ExpenseItem {
  id: string;
  category: ExpenseCategory['key'];
  name: string;
  amount?: number;
  frequency: 'monthly' | 'quarterly' | 'annually' | 'unknown';
  dueDate?: string;
  accountNumber?: string;
  autopay: boolean;
  provider?: string;
  website?: string;
  notes?: string;
  documents?: AppDocument[];
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame237: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const [expenses, setExpenses] = useState<ExpenseItem[]>(() => {
    try {
      const sessionExpenses = session.taskProgress?.['monthly_expenses']?.answers?.expenses || [];
      if (sessionExpenses.length > 0) {
        return sessionExpenses.map((expense: ExpenseItem) => ({
          ...expense,
          documents: normalizeDocuments(expense.documents || []),
        }));
      }

      const backup = localStorage.getItem('monthly-expenses-backup');
      if (backup) {
        const parsed = JSON.parse(backup);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((expense: ExpenseItem) => ({
            ...expense,
            documents: normalizeDocuments(expense.documents || []),
          }));
        }
      }
    } catch (error) {
      console.error('[Frame237] Error loading expenses:', error);
    }
    return [];
  });

  useEffect(() => {
    const sessionExpenses = session.taskProgress?.['monthly_expenses']?.answers?.expenses;
    if (sessionExpenses) {
      const normalizedExpenses = sessionExpenses.map((expense: ExpenseItem) => ({
        ...expense,
        documents: normalizeDocuments(expense.documents || []),
      }));

      if (JSON.stringify(normalizedExpenses) !== JSON.stringify(expenses)) {
        console.log('[Frame237] Syncing expenses from session:', normalizedExpenses);
        setExpenses(normalizedExpenses);
      }
    }
  }, [session.taskProgress?.['monthly_expenses']?.answers?.expenses]);

  const persistExpenses = async (updatedExpenses: ExpenseItem[]) => {
    try {
      localStorage.setItem('monthly-expenses-backup', JSON.stringify(updatedExpenses));
    } catch (error) {
      console.error('[Frame237] Failed to save expenses backup:', error);
    }

    await updateTaskProgress('monthly_expenses', { expenses: updatedExpenses }, 'in-progress');
    setExpenses(updatedExpenses);
  };

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame237] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  // Form state
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState<ExpenseItem['frequency']>('monthly');
  const [dueDate, setDueDate] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [autopay, setAutopay] = useState(false);
  const [provider, setProvider] = useState('');
  const [website, setWebsite] = useState('');
  const [notes, setNotes] = useState('');
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  const categories: ExpenseCategory[] = [
    {
      key: 'credit_card',
      label: 'Credit Card\nPayments',
      Icon: CreditCardIcon,
      description: 'Monthly credit card payments and minimum payment requirements'
    },
    {
      key: 'utilities',
      label: 'Utility\nBills',
      Icon: LightBulbIcon,
      description: 'Electric, gas, water, internet, phone, and other utility services'
    },
    {
      key: 'insurance',
      label: 'Insurance\nPremiums',
      Icon: ShieldIcon,
      description: 'Health, life, auto, home, and other insurance premiums'
    },
    {
      key: 'subscriptions',
      label: 'Recurring\nSubscriptions',
      Icon: SubscriptionIcon,
      description: 'Streaming services, memberships, and other recurring charges'
    },
    {
      key: 'transportation',
      label: 'Transportation\nExpenses',
      Icon: CarIcon,
      description: 'Car payments, public transit passes, and other transportation costs'
    },
    {
      key: 'healthcare',
      label: 'Healthcare\nExpenses',
      Icon: MedicalIcon,
      description: 'Regular medical expenses, prescriptions, and healthcare services'
    },
    {
      key: 'other',
      label: 'Other\nExpenses',
      Icon: BankIcon,
      description: 'Any other regular monthly expenses not covered by other categories'
    }
  ];

  const resetForm = () => {
    setName('');
    setAmount('');
    setFrequency('monthly');
    setDueDate('');
    setAccountNumber('');
    setAutopay(false);
    setProvider('');
    setWebsite('');
    setNotes('');
    setDocuments([]);
    setEditingExpenseId(null);
    setIsUploadingDocuments(false);
  };

  const handleCategoryClick = (category: ExpenseCategory) => {
    setSelectedCategory(category);
    resetForm();
    setIsModalOpen(true);
  };

  const handleFileUpload = async (files: File[]) => {
    if (!files.length) return;

    setIsUploadingDocuments(true);

    try {
      const uploadedDocs = await Promise.all(
        files.map((file) => documentService.upload(file))
      );

      setDocuments((prev) => [...prev, ...uploadedDocs]);
    } catch (error) {
      console.error('[Frame237] Failed to upload document(s):', error);
      alert('Failed to upload one or more documents.');
    } finally {
      setIsUploadingDocuments(false);
    }
  };

  const handleEditExpense = (expense: ExpenseItem) => {
    setEditingExpenseId(expense.id);
    setSelectedCategory(categories.find(c => c.key === expense.category) || null);
    setName(expense.name);
    setAmount(typeof expense.amount === 'number' ? expense.amount.toString() : '');
    setFrequency(expense.frequency);
    setDueDate(expense.dueDate || '');
    setAccountNumber(expense.accountNumber || '');
    setAutopay(expense.autopay);
    setProvider(expense.provider || '');
    setWebsite(expense.website || '');
    setNotes(expense.notes || '');
    setDocuments(normalizeDocuments(expense.documents || []));
    setIsModalOpen(true);
  };

  const handleDeleteExpense = async (expenseId: string) => {
    if (!confirm('Are you sure you want to delete this expense entry?')) {
      return;
    }

    const expenseToDelete = expenses.find((e) => e.id === expenseId);
    const attachedDocs = expenseToDelete?.documents || [];

    for (const doc of attachedDocs) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame237] Failed to delete document while deleting expense:', error);
      }
    }

    const updatedExpenses = expenses.filter(e => e.id !== expenseId);
    await persistExpenses(updatedExpenses);
  };

  const handleRemoveAttachedDocument = async (expenseId: string, documentIndex: number) => {
    const expense = expenses.find((e) => e.id === expenseId);
    const docToRemove = expense?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame237] Failed to delete saved document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedExpenses = expenses.map((expense) => {
      if (expense.id !== expenseId) return expense;
      return {
        ...expense,
        documents: normalizeDocuments(expense.documents || []).filter((_, index) => index !== documentIndex),
      };
    });

    await persistExpenses(updatedExpenses);
  };

  const handleSave = async () => {
    if (!selectedCategory || !name) {
      alert('Please fill in all required fields');
      return;
    }

    const normalizedAmount = amount.trim() ? parseFloat(amount.replace(/,/g, '')) : undefined;

    const expenseData: ExpenseItem = {
      id: editingExpenseId || Date.now().toString(),
      category: selectedCategory.key,
      name,
      ...(normalizedAmount !== undefined ? { amount: normalizedAmount } : {}),
      frequency,
      dueDate,
      accountNumber,
      autopay,
      provider,
      website,
      notes,
      documents,
    };

    const updatedExpenses = editingExpenseId
      ? expenses.map(e => e.id === editingExpenseId ? expenseData : e)
      : [...expenses, expenseData];

    await persistExpenses(updatedExpenses);

    setIsModalOpen(false);
    resetForm();
  };

  const handleNext = () => {
    navigate('/frame-238');

    setTimeout(() => {
      void updateTaskProgress('monthly_expenses', { expenses: expenses ?? [] }, 'in-progress')
        .catch((e) => console.error('[Frame237] Failed to save monthly expenses progress:', e));
    }, 0);
  };

  const handleBack = () => navigate('/frame-235');

  const renderDocuments = (docs: AppDocument[] = [], expenseId: string) => {
    const normalizedDocs = normalizeDocuments(docs);
    if (!normalizedDocs || normalizedDocs.length === 0) return null;

    return (
      <div className="mt-3 space-y-2">
        <div className="space-y-2">
          {normalizedDocs.map((doc, index) => (
            <div
              key={`${expenseId}-doc-${doc.id || index}-${index}`}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2"
            >
              <button
                type="button"
                onClick={() => openDocument(doc)}
                className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
              >
                <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                <span
                  className="text-sm font-medium text-stone-800 truncate"
                  title={doc.name}
                >
                  {doc.name}
                </span>
              </button>

              <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                <button
                  type="button"
                  onClick={() => downloadDocument(doc)}
                  className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                  title="Download document"
                >
                  Download
                </button>

                <button
                  type="button"
                  onClick={() => handleRemoveAttachedDocument(expenseId, index)}
                  className="text-red-500 hover:text-red-700 text-xs shrink-0"
                  title="Remove document"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const removeDocumentFromForm = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame237] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <ResponsiveHeader title="Track Your Monthly Expenses" />
        <div className="max-w-4xl">
          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] mb-12">
            Document your recurring monthly expenses and bills. This information will help your loved ones maintain necessary payments and manage your accounts.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map(category => (
              <button
                key={category.key}
                onClick={() => handleCategoryClick(category)}
                className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <category.Icon className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-stone-800 whitespace-pre-line leading-tight">
                      {category.label}
                    </h3>
                    <p className="text-sm text-stone-600 mt-1">
                      {category.description}
                    </p>
                    {expenses.filter(e => e.category === category.key).length > 0 && (
                      <div className="mt-2 text-cyan-600 text-sm font-medium">
                        {expenses.filter(e => e.category === category.key).length} items added
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {expenses.length > 0 && (
            <div className="mt-12">
              <h2 className="text-stone-800 text-xl font-semibold font-['Work_Sans'] mb-4">
                Added Monthly Expenses
              </h2>
              <div className="space-y-4">
                {expenses.map(expense => {
                  const category = categories.find(c => c.key === expense.category);
                  const frequencyLabel = expense.frequency === 'unknown'
                    ? 'Unknown'
                    : expense.frequency.charAt(0).toUpperCase() + expense.frequency.slice(1);
                  const detailParts: string[] = [];
                  if (typeof expense.amount === 'number') {
                    detailParts.push(`$${expense.amount.toLocaleString()}/${frequencyLabel}`);
                  } else {
                    detailParts.push(frequencyLabel);
                  }
                  if (expense.dueDate) {
                    detailParts.push(`Due: ${new Date(expense.dueDate).toLocaleDateString()}`);
                  }
                  if (expense.accountNumber) {
                    detailParts.push(`Account ending in ${expense.accountNumber.slice(-4)}`);
                  }
                  if (expense.autopay) {
                    detailParts.push('AutoPay Enabled');
                  }
                  return (
                    <div key={expense.id} className="bg-white rounded-lg border border-neutral-200 p-4">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex flex-col gap-2 min-w-0 flex-1 sm:flex-row sm:items-start sm:gap-4">
                          {category && <category.Icon className="w-6 h-6 text-cyan-700 flex-shrink-0" />}

                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-stone-800 break-words">
                              {expense.name}
                            </div>

                            {detailParts.length > 0 && (
                              <div className="text-sm text-stone-500 mt-1 break-words">
                                {detailParts.join(' • ')}
                              </div>
                            )}

                            {expense.provider && (
                              <div className="text-sm text-stone-600 mt-1 break-words">
                                Provider: {expense.provider}
                                {expense.website && (
                                  <a
                                    href={expense.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-2 text-cyan-600 hover:text-cyan-700"
                                  >
                                    Visit Website →
                                  </a>
                                )}
                              </div>
                            )}

                            {expense.notes && (
                              <div className="text-sm text-stone-600 mt-2 break-words">
                                {expense.notes}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
                          <button
                            onClick={() => handleEditExpense(expense)}
                            className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteExpense(expense.id)}
                            className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {renderDocuments(expense.documents || [], expense.id)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </ResponsiveContent>

      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingExpenseId ? 'Edit Expense Entry' : (selectedCategory ? `Add ${selectedCategory.label.replace('\n', ' ')}` : 'Add Expense')}
        okText={editingExpenseId ? 'Save Changes' : 'Add Expense'}
        cancelText="Cancel"
        onOk={handleSave}
        onCancel={() => {
          setIsModalOpen(false);
          resetForm();
        }}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Expense Name*</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              placeholder="Enter expense name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Estimated Amount (optional)</label>
            <p className="text-xs text-stone-500 mb-2">Monthly amounts may vary. This helps your executor identify recurring obligations.</p>
            <input
              type="text"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ''))}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              placeholder="Enter amount"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Frequency*</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as ExpenseItem['frequency'])}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annually">Annually</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Due Date (optional)</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Account Number (optional)</label>
            <input
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              placeholder="Enter account number"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="autopay"
              checked={autopay}
              onChange={(e) => setAutopay(e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
            />
            <label htmlFor="autopay" className="ml-2 block text-sm text-stone-700">AutoPay Enabled</label>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Provider/Company (optional)</label>
            <input
              type="text"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              placeholder="Enter provider name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Website (optional)</label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              placeholder="https://..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              rows={3}
              placeholder="Add any additional notes"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Upload Documents (optional)</label>
            <FileUpload onUpload={handleFileUpload} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" multiple />

            {isUploadingDocuments && (
              <div className="mt-3 text-sm text-stone-500">
                Uploading documents...
              </div>
            )}

            {documents && documents.length > 0 && (
              <div className="mt-3 space-y-2">
                {documents.map((doc, idx) => (
                  <div key={`${doc.id || idx}-${idx}`} className="flex flex-col sm:flex-row sm:items-center justify-between text-sm bg-stone-50 p-2 border border-stone-200 rounded gap-3">
                    <button
                      type="button"
                      onClick={() => openDocument(doc)}
                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
                    >
                      <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                      <span className="text-stone-600 truncate" title={doc.name}>
                        {doc.name}
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                      >
                        Download
                      </button>

                      <button
                        type="button"
                        onClick={() => removeDocumentFromForm(idx)}
                        className="text-red-600 hover:text-red-800 text-xs font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <ResponsiveFooter
        title="Track Debts and Final Expenses"
        subtitle="Track your recurring monthly expenses"
        progressPercent={66.67}
        onNext={handleNext}
        onBack={handleBack}
        nextText="Next"
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame237;